import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash-es';
import Collapse from 'reactstrap/lib/Collapse';
import Button from 'reactstrap/lib/Button';
import FormGroup from 'reactstrap/lib/FormGroup';
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import { FormFieldMask } from 'site-modules/shared/components/form-field-mask/form-field-mask';
import { PHONE_MASK_V2 } from 'site-modules/shared/constants/lead-form/validation';
import { getUserInfoFromProfile } from 'site-modules/shared/components/lead-form/utils/utils';
import { PrivacyNote } from 'site-modules/shared/components/lead-form/unified-lead-form/components/privacy-note/privacy-note';
import {
  LeadFormCheckboxPropTypes,
  LeadFormCheckboxDefaultProps,
  CollapseStatePropTypes,
} from 'site-modules/shared/components/lead-form/lead-form-prop-types/lead-form-prop-types';
import { COLLAPSE_STATE } from 'site-modules/shared/constants/lead-form/lead-form-params';
import { ErrorFieldCopy } from 'site-modules/shared/components/lead-form/error-field-copy/error-field-copy';
import {
  DISCLAIMER_TYPES,
  withDynamicDisclaimer,
} from 'site-modules/shared/components/lead-form/utils/with-dynamic-disclaimer';

import './user-info.scss';

const groupClassName = 'mb-0';
const fieldClassName = 'w-100 mb-0';
const fieldLabelClassName = 'text-gray-darker mb-0';
const fieldErrorClassName = 'medium text-danger';
const ENTER_KEY_EVENT = {
  CODE: 13,
  NAME: 'Enter',
};
const FORM_LABELS_PLACEHOLDERS_LEAD_FORM = {
  nameLabel: 'First Name',
  lastNameLabel: 'Last Name',
  emailLabel: 'Email',
  phoneNumLabel: 'Phone',
};

export class UserInfoUI extends Component {
  static propTypes = {
    fieldRef: PropTypes.func.isRequired,
    validate: PropTypes.func.isRequired,
    validationErrors: PropTypes.shape({}),
    submitButtonText: PropTypes.string,
    enableComments: PropTypes.bool,
    applyPhoneMask: PropTypes.bool,
    showSubmitLoadingIndicator: PropTypes.bool,
    toggleableSubmitInfo: PropTypes.bool,
    initialCollapseState: CollapseStatePropTypes,
    profile: PropTypes.shape({}),
    userInfoOptions: PropTypes.shape({}),
    showCheckbox: PropTypes.func,
    nextButtonTrackingId: PropTypes.string,
    className: PropTypes.string,
    checkbox: LeadFormCheckboxPropTypes,
    tcpaParentDealershipName: PropTypes.string,
    resetFieldValidation: PropTypes.func,
    isSingleSelect: PropTypes.bool,
    disclaimerType: PropTypes.oneOf([DISCLAIMER_TYPES.BASIC, DISCLAIMER_TYPES.SPECIFIC]),
  };

  static defaultProps = {
    validationErrors: {},
    enableComments: false,
    applyPhoneMask: false,
    showSubmitLoadingIndicator: false,
    submitButtonText: 'Submit',
    initialCollapseState: null,
    toggleableSubmitInfo: false,
    profile: {},
    userInfoOptions: {},
    showCheckbox: noop,
    nextButtonTrackingId: '',
    className: 'mb-0_5 rounded bg-gray-extra-light px-0_75 py-0_5',
    checkbox: LeadFormCheckboxDefaultProps,
    tcpaParentDealershipName: null,
    resetFieldValidation: null,
    isSingleSelect: false,
    disclaimerType: DISCLAIMER_TYPES.BASIC,
  };

  state = {
    hideUserInfo: this.props.toggleableSubmitInfo,
    collapseState: this.props.initialCollapseState,
  };

  onEmailEntered = () => this.emailInputRef && !this.emailInputRef.value() && this.emailInputRef.focus();

  /**
   * Toggles user info visibility.
   */
  toggleUserInfo = () => this.setState(({ hideUserInfo }) => ({ hideUserInfo: !hideUserInfo }));

  toggleFormFields = () => {
    this.setState({
      collapseState: null,
    });
  };

  emailFieldRef = field => {
    this.emailInputRef = field;
    return this.props.fieldRef(field);
  };

  firstNameFieldRef = field => this.props.fieldRef(field);

  handleKeyPress = e => {
    const { key, which, keyCode } = e;

    if (key === ENTER_KEY_EVENT.NAME || keyCode === ENTER_KEY_EVENT.CODE || which === ENTER_KEY_EVENT.CODE) {
      e.preventDefault();
      this.toggleFormFields();
    }
  };

  // eslint-disable-next-line class-methods-use-this
  handleTouchStart = e => {
    e.currentTarget.focus();
  };

  render() {
    const {
      fieldRef,
      validationErrors,
      validate,
      enableComments,
      applyPhoneMask,
      showSubmitLoadingIndicator,
      submitButtonText,
      toggleableSubmitInfo,
      profile,
      initialCollapseState,
      nextButtonTrackingId,
      showCheckbox,
      checkbox,
      tcpaParentDealershipName,
      className,
      userInfoOptions,
      resetFieldValidation,
      isSingleSelect,
      disclaimerType,
    } = this.props;

    const { hideUserInfo, collapseState } = this.state;
    const PhoneField = applyPhoneMask ? FormFieldMask : FormField;
    const phoneMask = applyPhoneMask ? PHONE_MASK_V2 : null;

    const firstNameValid = !('firstName' in validationErrors);
    const lastNameValid = !('lastName' in validationErrors);
    const emailValid = !('email' in validationErrors);
    const phoneValid = !('phone' in validationErrors);
    const selectedLeadsValid = !('selectedLeadsEmpty' in validationErrors);

    const { firstName, lastName, email, phoneNumber } = getUserInfoFromProfile(profile);

    let RestFieldsContainerTag = Fragment;
    let restFieldsContainerProps = {};

    if (initialCollapseState === COLLAPSE_STATE.PARTIAL_COLLAPSE) {
      RestFieldsContainerTag = Collapse;
      restFieldsContainerProps = { isOpen: !collapseState, onEntered: this.onEmailEntered };
    }

    const {
      className: customClassName,
      inputClassName,
      btnColor = 'primary-b',
      btnClassName = 'px-1_5 px-sm-2',
      isLabelInside,
    } = userInfoOptions;

    const fieldProps = {
      className: fieldClassName,
      labelClassName: classnames(fieldLabelClassName, { 'pos-a top-0 left-0 mt-0_5 ms-1 small': isLabelInside }),
      inputClassName: classnames('inspectletIgnore', inputClassName, { 'size-16 pt-1_5': isLabelInside }),
      'aria-required': true,
      onBlur: validate,
      onChange: resetFieldValidation,
      resetTouchOnChange: true,
    };

    const firstLastNameFields = (
      <Fragment>
        <FormGroup className={classnames(groupClassName, 'form-group')} noMargin>
          <FormField
            {...fieldProps}
            id="lead-form-fname"
            name="firstName"
            placeholder={FORM_LABELS_PLACEHOLDERS_LEAD_FORM.namePlaceholder}
            label={FORM_LABELS_PLACEHOLDERS_LEAD_FORM.nameLabel}
            ref={this.firstNameFieldRef}
            aria-invalid={!firstNameValid}
            aria-describedby="error-firstName"
            isValid={firstNameValid}
            maxLength={60}
            defaultValue={firstName}
            autoComplete="given-name"
          />
          <ErrorFieldCopy
            id="error-firstName"
            className={classnames(fieldErrorClassName, { 'mt-0_25 mb-0_5': !firstNameValid })}
            copy={validationErrors.firstName}
          />
        </FormGroup>
        <FormGroup className={classnames(groupClassName, 'form-group')} noMargin>
          <FormField
            {...fieldProps}
            id="lead-form-lname"
            name="lastName"
            placeholder={FORM_LABELS_PLACEHOLDERS_LEAD_FORM.lastNamePlaceholder}
            label={FORM_LABELS_PLACEHOLDERS_LEAD_FORM.lastNameLabel}
            ref={fieldRef}
            aria-invalid={!lastNameValid}
            aria-describedby="error-lastName"
            isValid={lastNameValid}
            maxLength={60}
            defaultValue={lastName}
            onKeyPress={restFieldsContainerProps.isOpen ? null : this.handleKeyPress}
            autoComplete="family-name"
          />
          <ErrorFieldCopy
            id="error-lastName"
            className={classnames(fieldErrorClassName, { 'mt-0_25 mb-0_5': !lastNameValid })}
            copy={validationErrors.lastName}
          />
        </FormGroup>
      </Fragment>
    );

    const emailPhoneFields = (
      <Fragment>
        <FormGroup className={classnames(groupClassName, 'form-group')} noMargin>
          <FormField
            {...fieldProps}
            id="lead-form-email"
            name="email"
            placeholder={FORM_LABELS_PLACEHOLDERS_LEAD_FORM.emailPlaceholder}
            label={FORM_LABELS_PLACEHOLDERS_LEAD_FORM.emailLabel}
            ref={this.emailFieldRef}
            aria-invalid={!emailValid}
            aria-describedby="error-email"
            isValid={emailValid}
            type="email"
            defaultValue={email}
            autoComplete="email"
          />
          <ErrorFieldCopy
            id="error-email"
            className={classnames(fieldErrorClassName, { 'mt-0_25 mb-0_5': !emailValid })}
            copy={validationErrors.email}
          />
        </FormGroup>
        <FormGroup className={classnames(groupClassName, 'form-group')} noMargin>
          <PhoneField
            {...fieldProps}
            id="lead-form-phone"
            name="phone"
            label={FORM_LABELS_PLACEHOLDERS_LEAD_FORM.phoneNumLabel}
            ref={fieldRef}
            aria-invalid={!phoneValid}
            aria-describedby="error-phone"
            isValid={phoneValid}
            type="tel"
            mask={phoneMask}
            defaultValue={phoneNumber}
            autoComplete="tel"
          />
          <ErrorFieldCopy
            id="error-phone"
            className={classnames(fieldErrorClassName, { 'mt-0_25 mb-0_5': !phoneValid })}
            copy={validationErrors.phone}
          />
        </FormGroup>
      </Fragment>
    );

    return (
      <fieldset className={classnames('user-info', className, customClassName)}>
        <legend className="visually-hidden">Your Contact Information</legend>
        {toggleableSubmitInfo && (
          <Fragment>
            {hideUserInfo ? (
              <Button
                color={btnColor}
                onClick={this.toggleUserInfo}
                className={classnames('submit-button btn-lg fw-medium', btnClassName)}
                data-tracking-id="expand_lead_form"
              >
                <span>{submitButtonText}</span>
              </Button>
            ) : (
              <div>
                <Button
                  type="submit"
                  color={btnColor}
                  className={classnames('submit-button btn-lg mb-1 fw-medium', btnClassName, {
                    'spinner-active': showSubmitLoadingIndicator,
                  })}
                  disabled={showSubmitLoadingIndicator}
                >
                  <i className="spinner icon-spinner8 me-0_25" aria-hidden />
                  <span>{submitButtonText}</span>
                </Button>
                <PrivacyNote
                  tcpaParentDealershipName={tcpaParentDealershipName}
                  ctaText={submitButtonText}
                  isSingleSelect={isSingleSelect}
                />
              </div>
            )}
          </Fragment>
        )}
        {!hideUserInfo && (
          <Fragment>
            {firstLastNameFields}
            <RestFieldsContainerTag {...restFieldsContainerProps}>
              {emailPhoneFields}
              {enableComments && (
                <FormGroup className="form-group" noMargin>
                  <FormField
                    label="Comments"
                    id="lead-form-comment"
                    name="comments"
                    tag="textarea"
                    ref={fieldRef}
                    className={fieldClassName}
                    labelClassName={fieldLabelClassName}
                    maxLength={1000}
                  />
                </FormGroup>
              )}
              {showCheckbox() && (
                <FormGroup className="d-flex align-items-center form-group" noMargin>
                  <FormField
                    id="lead-form-checkbox"
                    name="checkbox"
                    type="checkbox"
                    ref={fieldRef}
                    className="d-inline-block mb-0"
                    defaultChecked={checkbox.defaultChecked}
                  />
                  <label
                    className="ms-0_5 text-gray-darker mb-0 d-inline-block medium lead-form-checkbox"
                    htmlFor="lead-form-checkbox"
                  >
                    {checkbox.label}
                  </label>
                </FormGroup>
              )}
            </RestFieldsContainerTag>
          </Fragment>
        )}
        {!toggleableSubmitInfo && (
          <div>
            {!selectedLeadsValid && <div className="text-danger medium">{validationErrors.selectedLeadsEmpty}</div>}
            {disclaimerType === DISCLAIMER_TYPES.SPECIFIC && !collapseState && (
              <PrivacyNote
                tcpaParentDealershipName={tcpaParentDealershipName}
                classNames="mb-1"
                ctaText={submitButtonText}
                isSingleSelect={isSingleSelect}
                disclaimerType={disclaimerType}
              />
            )}
            {!collapseState && (
              <Button
                type="submit"
                color={btnColor}
                onTouchStart={this.handleTouchStart}
                className={classnames('submit-button btn-lg fw-medium', btnClassName, {
                  'spinner-active': showSubmitLoadingIndicator,
                })}
                disabled={showSubmitLoadingIndicator}
              >
                <i className="spinner icon-spinner8 me-0_25" aria-hidden />
                <span>{submitButtonText}</span>
              </Button>
            )}
            {!!collapseState && (
              <Button
                color={btnColor}
                className={classnames('next-button fw-medium', btnClassName)}
                size="lg"
                onClick={this.toggleFormFields}
                {...(nextButtonTrackingId
                  ? {
                      'data-tracking-id': nextButtonTrackingId,
                    }
                  : {})}
                data-test="next-btn"
              >
                Next
              </Button>
            )}
            {disclaimerType === DISCLAIMER_TYPES.BASIC && (
              <div className="mt-1">
                <PrivacyNote
                  tcpaParentDealershipName={tcpaParentDealershipName}
                  classNames="mb-1"
                  ctaText={submitButtonText}
                  isSingleSelect={isSingleSelect}
                  disclaimerType={disclaimerType}
                />
              </div>
            )}
          </div>
        )}
      </fieldset>
    );
  }
}

export const UserInfo = withDynamicDisclaimer(UserInfoUI);
